import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { projectProgressListColumns } from "@patronix/domain/list";
import type { ListTools } from "@/components/ListToolbar";
import { DataList, type Column } from "@/components/DataList";
import { Badge } from "@/components/ui";
import {
  isProjectFilter,
  listProgress,
  progressQuery,
  projectFilters,
  projectStatuses,
  projectTone,
  share,
  type ProjectFilter,
  type ProjectProgressRow,
} from "@/features/project/api";
import { activeCompanyOf, useActiveCompany } from "@/lib/activeCompany";
import { filteredQuery, filterSortRows, type ClientColumn } from "@/lib/clientList";
import { cn } from "@/lib/cn";
import { type ExportColumn } from "@/lib/exportXlsx";
import { useFormat } from "@/lib/format";
import { listView, viewSearch, type ViewSearch } from "@/lib/listView";
import { sessionContextQuery } from "@/lib/session";

// Project → Progress (PX-PRJ-120): every project of the company with earned
// hours, milestones and the quantity progress of its pegged documents. One
// RPC (project_progress_page, ≤ 500 projects); computed, so export only.
// A row opens the project.

interface Search extends ViewSearch {
  status: ProjectFilter;
}

// Computed per project and loaded whole: filters and sort run in the browser.
const clientColumns: ClientColumn<ProjectProgressRow>[] = [
  { key: "code", type: "text", value: (r) => r.code },
  { key: "name", type: "text", value: (r) => r.name },
  { key: "hours", type: "number", value: (r) => share(r.earned_hours, r.planned_hours) },
  { key: "milestones", type: "number", value: (r) => share(r.milestones_done, r.milestones) },
  { key: "purchase", type: "number", value: (r) => share(r.purchase_done, r.purchase_qty) },
  { key: "production", type: "number", value: (r) => share(r.production_done, r.production_qty) },
  { key: "sales", type: "number", value: (r) => share(r.sales_done, r.sales_qty) },
  { key: "status", type: "enum", value: (r) => r.status },
];

export const Route = createFileRoute("/_app/project/progress")({
  validateSearch: (raw: Record<string, unknown>): Search => ({
    status: isProjectFilter(raw.status) ? raw.status : "open",
    ...viewSearch(projectProgressListColumns, raw),
  }),
  loaderDeps: ({ search }) => ({ status: search.status }),
  loader: async ({ context, deps }) => {
    const ctx = await context.queryClient.ensureQueryData(sessionContextQuery);
    const companyId = activeCompanyOf(ctx)?.id;
    if (companyId) await context.queryClient.ensureInfiniteQueryData(progressQuery(companyId, deps.status));
  },
  component: ProgressPage,
});

function Bar({ value, label }: { value: number | null; label: string }) {
  if (value === null) return <span className="text-ink-faint">—</span>;
  return (
    <span className="flex items-center gap-2" title={label}>
      <span className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-surface-muted" aria-hidden="true">
        <span className={cn("block h-full rounded-full", value >= 100 ? "bg-(--status-success-fg)" : "bg-accent")} style={{ width: `${value}%` }} />
      </span>
      <span className="tabular text-xs">{value}%</span>
      <span className="sr-only">{label}</span>
    </span>
  );
}

function ProgressPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const go = useNavigate();
  const { company } = useActiveCompany();
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const setStatus = useCallback((status: ProjectFilter) => setSearch({ status }), [setSearch]);
  const view = listView(projectProgressListColumns, search, setSearch);
  const query = filteredQuery(useInfiniteQuery(progressQuery(company?.id ?? "", search.status)), clientColumns, view.query.filters, view.query.sort);
  const docs = (open: number, total: number) => (total ? t("project.progress.docs", { open, total }) : "");

  const columns: Column<ProjectProgressRow>[] = [
    {
      key: "code",
      header: t("project.colCode"),
      width: "minmax(110px,0.8fr)",
      cell: (r) => <span className="font-mono text-xs">{r.code}</span>,
    },
    {
      key: "name",
      header: t("project.colName"),
      width: "minmax(160px,1.4fr)",
      cell: (r) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate">{r.name}</span>
          {r.customer_name && <span className="truncate text-xs text-ink-muted">{r.customer_name}</span>}
        </span>
      ),
    },
    {
      key: "hours",
      header: t("project.progress.hours"),
      width: "minmax(140px,1fr)",
      cell: (r) => (
        <span className="flex flex-col gap-0.5">
          <Bar value={share(r.earned_hours, r.planned_hours)} label={t("project.progress.hours")} />
          <span className="tabular text-[11px] text-ink-muted">
            {t("project.bookedOfPlanned", { booked: fmt.qty(String(r.booked_hours)), planned: fmt.qty(String(r.planned_hours)) })}
          </span>
        </span>
      ),
    },
    {
      key: "milestones",
      header: t("project.progress.milestones"),
      width: "minmax(90px,0.6fr)",
      align: "end",
      cell: (r) => (r.milestones ? <span className="tabular">{`${r.milestones_done} / ${r.milestones}`}</span> : "—"),
    },
    {
      key: "purchase",
      header: t("project.progress.purchase"),
      width: "minmax(120px,0.9fr)",
      cell: (r) => (
        <span className="flex flex-col gap-0.5">
          <Bar value={share(r.purchase_done, r.purchase_qty)} label={t("project.progress.purchase")} />
          <span className="text-[11px] text-ink-muted">{docs(r.purchase_open, r.purchase_lines)}</span>
        </span>
      ),
    },
    {
      key: "production",
      header: t("project.progress.production"),
      width: "minmax(120px,0.9fr)",
      cell: (r) => (
        <span className="flex flex-col gap-0.5">
          <Bar value={share(r.production_done, r.production_qty)} label={t("project.progress.production")} />
          <span className="text-[11px] text-ink-muted">{docs(r.production_open, r.production_orders)}</span>
        </span>
      ),
    },
    {
      key: "sales",
      header: t("project.progress.sales"),
      width: "minmax(120px,0.9fr)",
      cell: (r) => (
        <span className="flex flex-col gap-0.5">
          <Bar value={share(r.sales_done, r.sales_qty)} label={t("project.progress.sales")} />
          <span className="text-[11px] text-ink-muted">{docs(r.sales_open, r.sales_lines)}</span>
        </span>
      ),
    },
    {
      key: "status",
      header: t("po.colStatus"),
      width: "minmax(96px,0.6fr)",
      cell: (r) => <Badge tone={projectTone[r.status]}>{t(`project.status.${r.status}`)}</Badge>,
      options: projectStatuses.map((v) => ({ value: v, label: t(`project.status.${v}`) })),
    },
  ];
  const exportColumns: ExportColumn<ProjectProgressRow>[] = [
    { header: "project_code", kind: "code", value: (r) => r.code },
    { header: "project_name", kind: "text", value: (r) => r.name },
    { header: "status", kind: "code", value: (r) => r.status },
    { header: "planned_hours", kind: "number", value: (r) => Number(r.planned_hours) },
    { header: "booked_hours", kind: "number", value: (r) => Number(r.booked_hours) },
    { header: "earned_hours", kind: "number", value: (r) => Number(r.earned_hours) },
    { header: "milestones", kind: "integer", value: (r) => r.milestones },
    { header: "milestones_done", kind: "integer", value: (r) => r.milestones_done },
    { header: "purchase_lines", kind: "integer", value: (r) => r.purchase_lines },
    { header: "purchase_received", kind: "number", value: (r) => Number(r.purchase_done), sum: false },
    { header: "purchase_ordered", kind: "number", value: (r) => Number(r.purchase_qty), sum: false },
    { header: "production_orders", kind: "integer", value: (r) => r.production_orders },
    { header: "production_reported", kind: "number", value: (r) => Number(r.production_done), sum: false },
    { header: "production_planned", kind: "number", value: (r) => Number(r.production_qty), sum: false },
    { header: "sales_lines", kind: "integer", value: (r) => r.sales_lines },
    { header: "sales_shipped", kind: "number", value: (r) => Number(r.sales_done), sum: false },
    { header: "sales_ordered", kind: "number", value: (r) => Number(r.sales_qty), sum: false },
    { header: "stock_lines", kind: "integer", value: (r) => r.stock_lines },
  ];
  const label = (f: ProjectFilter) => (f === "all" ? t("po.filterAll") : t(`project.filter.${f}`));

  const listTools: ListTools<ProjectProgressRow> = {
    id: "project-progress",
    title: t("project.progress.title"),
    export: { fileBase: "project-progress", sheet: t("project.progress.title"), columns: exportColumns, fetchPage: async (_cursor, signal) => {
              const page = await listProgress(company?.id ?? "", search.status, signal);
              return { ...page, rows: filterSortRows(page.rows, clientColumns, view.query.filters, view.query.sort) };
            } },
  };

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.project.title")} <span aria-hidden="true">/</span> {t("nav.project.progress")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("project.progress.title")}</h1>
        </div>
      </header>
      <p className="-mt-2 text-xs text-ink-muted">{t("project.progress.hint")}</p>
      <div role="group" aria-label={t("po.colStatus")} className="flex flex-wrap gap-2">
        {projectFilters.map((f) => (
          <button
            key={f}
            type="button"
            aria-pressed={search.status === f}
            onClick={() => setStatus(f)}
            className={cn(
              "h-8 rounded-full border px-3 text-[13px] transition-colors",
              search.status === f ? "border-ink bg-ink font-medium text-surface" : "border-border bg-surface text-ink hover:bg-surface-hover",
            )}
          >
            {label(f)}
          </button>
        ))}
      </div>
      <div className="flex min-h-0 grow">
        <DataList
          label={t("project.progress.listLabel")}
          columns={columns}
          query={query}
          rowId={(r) => r.id}
          selectedId={undefined}
          onSelect={(r) => void go({ to: "/project/projects", search: { status: "all", q: "", id: r.id } })}
          onClear={() => undefined}
          emptyTitle={t("project.emptyTitle")}
          emptyBody={t("project.progress.empty")}
          minWidth={980}
          tools={listTools}
          view={view}
        />
      </div>
    </div>
  );
}
